'use client';

import type { ReactNode } from 'react';
import { cn } from '@app/ui/_lib/utils';
import { useAtom } from 'jotai';
import { tv } from 'tailwind-variants';
import { atomWithStorage } from 'jotai/utils';
import { Button } from '../../Inputs/Button';
import { IconResolver } from '../../IconResolver';
import { Container } from '../../Container';

export type ContentLayoutProps = {
	className?: string;
	children?: ReactNode;
	sidebar?: ReactNode;
};

export const styles = tv({
	slots: {
		base: 'grid lg:min-h-screen lg:transition-[grid-template-columns] lg:duration-normal lg:ease-smooth',
		sidebar: 'transition-opacity duration-normal ease-smooth',
	},
	variants: {
		sidebarOpen: {
			true: {
				base: 'lg:grid-cols-[auto_20rem] xl:grid-cols-[auto_25rem]',
			},
			false: {
				base: 'lg:grid-cols-[auto_0rem]',
				sidebar: 'opacity-0 pointer-events-none',
			},
		},
	},
});

const sidebarOpenAtom = atomWithStorage('sidebarOpen', false);

export function ContentLayout(props: ContentLayoutProps) {
	const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenAtom);
	const { base, sidebar } = styles({ sidebarOpen: sidebarOpen });

	return (
		<section className={`overflow-clip`} data-component={'ContentLayout'}>
			<Container>
				<div className={`max-lg:hidden lg:flex lg:sticky lg:top-[9rem] lg:right-0 lg:z-50 lg:pointer-events-none`}>
					<Button
						parent='ContentLayout'
						aria-label={sidebarOpen ? 'Open sidebar' : 'Close sidebar'}
						className={`lg:pointer-events-auto lg:ml-auto`}
						onClick={() => setSidebarOpen(prev => !prev)}
						styleType='ghost'
						iconOnly={true}
						styleSize='xSmall'
					>
						{sidebarOpen ? (
							<IconResolver icon={'arrow-line-right'} className='w-3.5 h-3.5' />
						) : (
							<IconResolver icon={'arrow-line-left'} className='w-3.5 h-3.5' />
						)}
					</Button>
				</div>
				<div className={base()}>
					<article className={`pt-28 lg:pt-[9rem] min-w-0`}>
						<div className={cn('flex flex-col', props.className)}>{props.children}</div>
					</article>
					<aside className={sidebar({ className: 'no-nested-containers max-lg:hidden lg:sticky lg:top-[9rem] lg:h-[calc(100vh-0rem)]' })}>
						<div className={`lg:h-full lg:pl-12 lg:w-[20rem] xl:w-[25rem] xl:pl-16`}>{props.sidebar}</div>
					</aside>
				</div>
			</Container>
		</section>
	);
}
